import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { navSections } from "./portalData";
import {
  CARD_STATEMENTS_KEY,
  CREDIT_CARDS_KEY,
  CREDIT_CARD_DATA_CHANGED_EVENT,
  countIncompleteCardStatementsForUser,
} from "../../pages/Settings/CreditCards/creditCardsData";
import "./portal-menu.css";

/* Left menu ported from the prototype (inc/menu.html): white sidebar, red
   active state, count badges, Administration dropdown, and the user footer. */

const PAGE_TITLES = [
  ["/claim/credit-card-statements", "Credit Card Statement"],
  ["/dashboard", "Dashboard"],
  ["/travel", "Travel & Budget"],
  ["/training", "Training"],
  ["/claim", "Claims"],
  ["/approvals", "Approvals"],
  ["/finance", "Finance"],
  ["/report", "Reports"],
  ["/users", "Users"],
  ["/role", "Roles & Permission"],
  ["/departments", "Departments"],
  ["/designation", "Designation"],
  ["/tcategories", "Training Categories"],
  ["/budget", "Budget Codes"],
  ["/credit-cards", "Credit Cards"],
  ["/claimpolicy", "Claim Policy"],
  ["/audit", "Audit Trail"],
];

const CURRENT_USER_NAME = "Eldren Aniell";

const sectionForPath = (path) => {
  if (path.startsWith("/travel/create") || /^\/travel\/[^/]+\/amend$/.test(path)) return "Travel & Budget";
  if (/^\/(users|role|departments|designation|tcategories|budget|credit-cards|claimpolicy|audit|finance|report)/.test(path)) {
    return "Administration";
  }
  if (path.startsWith("/dashboard")) return "";
  return "Operations";
};

const titleForPath = (path) => {
  if (path.startsWith("/travel/create")) return "New travel request";
  if (/^\/travel\/[^/]+\/amend$/.test(path)) return "Amend approved travel request";
  return (PAGE_TITLES.find(([prefix]) => path.startsWith(prefix)) || [null, "Workspace"])[1];
};

const PortalLayout = ({ children }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const authenticatedProfile = useSelector(
    (state) => state.Login?.verifyAdmin?.user?.data?.profile,
  );
  const currentUserName =
    authenticatedProfile?.name ||
    authenticatedProfile?.full_name ||
    authenticatedProfile?.fullName ||
    [authenticatedProfile?.first_name, authenticatedProfile?.last_name]
      .filter(Boolean)
      .join(" ") ||
    CURRENT_USER_NAME;
  const [openDropdowns, setOpenDropdowns] = useState(
    () => new Set(["Administration", ...(currentPath.startsWith("/claim") ? ["Claims"] : [])]),
  );
  const [incompleteCardStatementCount, setIncompleteCardStatementCount] = useState(
    () => countIncompleteCardStatementsForUser(currentUserName),
  );

  useEffect(() => {
    const refreshCount = () =>
      setIncompleteCardStatementCount(
        countIncompleteCardStatementsForUser(currentUserName),
      );
    const refreshFromStorage = (event) => {
      if (!event.key || [CREDIT_CARDS_KEY, CARD_STATEMENTS_KEY].includes(event.key)) {
        refreshCount();
      }
    };

    window.addEventListener(CREDIT_CARD_DATA_CHANGED_EVENT, refreshCount);
    window.addEventListener("storage", refreshFromStorage);
    return () => {
      window.removeEventListener(CREDIT_CARD_DATA_CHANGED_EVENT, refreshCount);
      window.removeEventListener("storage", refreshFromStorage);
    };
  }, [currentUserName]);

  const pageTitle = titleForPath(currentPath);
  const section = sectionForPath(currentPath);

  const isPathActive = (item) =>
    item.exact
      ? currentPath === item.path
      : currentPath === item.path || currentPath.startsWith(`${item.path}/`);

  const isChildActive = (item) => (item.children || []).some(isPathActive);

  const toggleDropdown = (label) =>
    setOpenDropdowns((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  return (
    <div className="bes-shell">
      <aside className="bes-sidebar">
        <div className="bes-sidebar-header">
          <img src="/logo.png" alt="BESarawak" className="bes-sidebar-logo" />
          <span className="bes-sidebar-brand">BESarawak</span>
        </div>

        <nav className="bes-sidebar-nav">
          {navSections.map((navSection) => (
            <div key={navSection.title || "main"}>
              {navSection.title ? <div className="bes-nav-group-title">{navSection.title}</div> : null}
              {navSection.items.map((item) =>
                item.children ? (
                  <div key={item.label} className="bes-nav-dropdown">
                    <button
                      type="button"
                      className={`bes-nav-item ${isChildActive(item) ? "active" : ""}`}
                      onClick={() => toggleDropdown(item.label)}
                    >
                      <i className={item.icon} />
                      <span>{item.label}</span>
                      {item.badge ? <span className="bes-nav-badge">{item.badge}</span> : null}
                      <i
                        className={`ri-arrow-down-s-line bes-nav-chevron ${
                          openDropdowns.has(item.label) || isChildActive(item) ? "is-open" : ""
                        }`}
                      />
                    </button>
                    {(openDropdowns.has(item.label) || isChildActive(item)) && (
                      <div className="bes-nav-submenu">
                        {item.children.map((child) => {
                          const badge =
                            child.path === "/claim/credit-card-statements"
                              ? incompleteCardStatementCount
                              : child.badge;
                          return (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              end={child.exact}
                              className={({ isActive }) =>
                                `bes-subnav-item ${isActive || isPathActive(child) ? "active" : ""}`
                              }
                            >
                              <span>{child.label}</span>
                              {badge ? <span className="bes-subnav-badge">{badge}</span> : null}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `bes-nav-item ${isActive || currentPath.startsWith(item.path) ? "active" : ""}`
                    }
                  >
                    <i className={item.icon} />
                    <span>{item.label}</span>
                    {item.badge ? <span className="bes-nav-badge">{item.badge}</span> : null}
                  </NavLink>
                )
              )}
            </div>
          ))}
        </nav>

        <div className="bes-sidebar-footer">
          <div className="bes-user-profile">
            <div className="bes-user-avatar">EA</div>
            <div>
              <span className="bes-user-name">{currentUserName}</span>
              <span className="bes-user-role">General Manager</span>
            </div>
          </div>
          <Link to="/login" className="bes-logout-link">
            <i className="ri-logout-box-r-line" /> Logout
          </Link>
        </div>
      </aside>

      <main className="bes-main">
        <header className="bes-topbar">
          <div className="bes-breadcrumb">
            {section ? (
              <>
                <span>{section}</span>
                <span className="bes-breadcrumb-sep">/</span>
              </>
            ) : null}
            <span className="bes-breadcrumb-current">{pageTitle}</span>
          </div>
        </header>

        <section className="bes-content">{children}</section>
      </main>
    </div>
  );
};

export default PortalLayout;
